/**
 * TestPersonaPanel — 개발자 테스트 페르소나 전환 패널 (DEV 전용)
 *
 * 단일 계정 멀티학원 전략(A안)의 짝꿍 UI. 시드(`supabase/seed/test_personas.sql`)로
 * 깐 역할(원장/관리자/매니저/선생님) × 티어(유료/체험/연체/무료) 학원들을
 * 한 번에 보고, 클릭으로 즉시 전환한다. 현재 역할·티어·동기화(syncEnabled)를 라이브 표시.
 *
 * - import.meta.env.DEV 에서만 마운트 (root.tsx) → 프로덕션엔 안 나감.
 * - 학원 전환은 워크스페이스 상태 변경뿐(DB 쓰기 없음) → 안전.
 *   구독/동기화는 전환 시 SubscriptionLoader+useSyncStore 가 자동 재계산.
 *
 * @module core/dev/TestPersonaPanel
 */

import { useState } from "react";
import { FlaskConical, ChevronDown, Check, RefreshCw } from "lucide-react";
import { cn } from "~/core/utils/cn";
import { useWorkspaceStore } from "~/core/store/useWorkspaceStore";
import { useSubscriptionStore } from "~/core/store/useSubscriptionStore";
import { useSyncEnabled } from "~/core/store/useSyncStore";

const ROLE_KO: Record<string, string> = {
  owner: "원장",
  admin: "관리자",
  manager: "매니저",
  teacher: "선생님",
};

// 구독 status → 티어 라벨 + 색
const TIER: Record<string, { label: string; cls: string }> = {
  active: { label: "유료", cls: "bg-success-tint text-success-ink" },
  trialing: { label: "체험", cls: "bg-info-tint text-info-ink" },
  past_due: { label: "연체", cls: "bg-warning-tint text-warning-ink" },
  canceled: { label: "해지", cls: "bg-error-tint text-error-ink" },
  none: { label: "무료", cls: "bg-elevated text-muted" },
};

function tierOf(status: string | null | undefined) {
  return TIER[status ?? "none"] ?? TIER["none"]!;
}

export function TestPersonaPanel() {
  const [open, setOpen] = useState(false);
  const active = useWorkspaceStore((s) => s.active);
  const academies = useWorkspaceStore((s) => s.academies);
  const switchToAcademy = useWorkspaceStore((s) => s.switchToAcademy);
  const isLoading = useWorkspaceStore((s) => s.isLoading);
  const subStatus = useSubscriptionStore((s) => s.academySubscription?.status);
  const syncEnabled = useSyncEnabled();

  const curRole = ROLE_KO[active.role] ?? active.role;
  const curTier = tierOf(active.type === "academy" ? subStatus : "none");

  return (
    // 위치를 스스로 잡는다 — 부모(root.tsx)에 의존하면 root 의 HMR 이 실패했을 때 칩이 통째로
    // 사라진다(실제로 발생). DevThemePanel 은 이 칩 오른쪽에 자기 힘으로 선다.
    <div className="fixed bottom-3 left-3 z-[60] select-none font-sans">
      {/* 펼친 패널 */}
      {open && (
        <div className="mb-2 w-72 rounded-xl surface-glass shadow-2xl overflow-hidden">
          {/* 현재 상태 */}
          <div className="px-3 py-2.5 border-b border-line bg-surface/40">
            <p className="text-[11px] font-semibold text-muted mb-1.5">현재 워크스페이스</p>
            <p className="text-xs font-medium text-main truncate" title={active.name}>{active.name || "—"}</p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-elevated text-muted">
                {curRole}
              </span>
              <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", curTier.cls)}>{curTier.label}</span>
              <span
                className={cn(
                  "px-1.5 py-0.5 rounded text-[10px] font-medium ml-auto",
                  syncEnabled
                    ? "bg-success-tint text-success-ink"
                    : "bg-elevated text-muted",
                )}
                title="세션 동기화(syncEnabled)"
              >
                동기화 {syncEnabled ? "ON" : "OFF"}
              </span>
            </div>
          </div>

          {/* 학원 전환 리스트 */}
          <div className="max-h-72 overflow-y-auto py-1">
            {academies.length === 0 && (
              <p className="px-3 py-3 text-[11px] text-subtle">학원 없음 — 시드를 먼저 실행하세요.</p>
            )}
            {academies.map((a) => {
              const isActive = a.id === active.id;
              return (
                <button
                  key={a.id}
                  type="button"
                  disabled={isLoading || isActive}
                  onClick={() => void switchToAcademy(a.id)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2 text-left transition-colors",
                    isActive ? "bg-accent/10" : "hover:bg-surface/50",
                    "disabled:cursor-default",
                  )}
                >
                  {isActive ? (
                    <Check className="w-3.5 h-3.5 text-accent shrink-0" />
                  ) : (
                    <span className="w-3.5 shrink-0" />
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs text-main truncate" title={a.name}>{a.name}</span>
                  </span>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-elevated text-muted shrink-0">
                    {ROLE_KO[a.role] ?? a.role}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="px-3 py-1.5 border-t border-line flex items-center gap-1.5">
            <RefreshCw className={cn("w-3 h-3 text-subtle", isLoading && "animate-spin")} />
            <span className="text-[10px] text-subtle">전환 시 구독·동기화 자동 반영</span>
          </div>
        </div>
      )}

      {/* 토글 칩 */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shadow-lg",
          "surface-glass text-main transition-colors",
        )}
        title="테스트 페르소나"
      >
        <FlaskConical className="w-3.5 h-3.5 text-accent" />
        <span className="text-[11px] font-semibold">{curRole}</span>
        <span className={cn("px-1 py-0.5 rounded text-[9px] font-medium", curTier.cls)}>{curTier.label}</span>
        <ChevronDown className={cn("w-3 h-3 text-subtle transition-transform", open && "rotate-180")} />
      </button>
    </div>
  );
}

export default TestPersonaPanel;
